/**
 * Parses workshop staff notification deeplinks (BR-NOT-05).
 * Supported:
 * - `zrsystemchat://conversations/{conversationId}`
 * - `zrsystem://check-in/{receptionId}`
 * - `zrsystem://check-out/{serviceOrderId}`
 * - `zrsystem://service-orders/{orderId}` (BR-SO-88/91)
 * - `zrsystem://quotes/{quoteId}` (BR-SO-89/94)
 * - `zrsystem://work-timer-overtime/{placeId}/{mechanicUserId}` (BR-SO-183)
 */
export type StaffNotificationDeeplink =
  | { kind: 'conversation'; conversationId: string }
  | { kind: 'check-in'; receptionId: string }
  | { kind: 'check-out'; serviceOrderId: string }
  | { kind: 'service-order'; orderId: string }
  | { kind: 'quote'; quoteId: string }
  | { kind: 'work-timer-overtime'; placeId: string; mechanicUserId: string };

export function parseStaffNotificationDeeplink(deeplink: string): StaffNotificationDeeplink | null {
  const trimmed = deeplink.trim();

  const conversation = /^zrsystemchat:\/\/conversations\/([0-9a-f-]{36})$/i.exec(trimmed);
  if (conversation?.[1] !== undefined) {
    return { kind: 'conversation', conversationId: conversation[1] };
  }

  const checkIn = /^zrsystem:\/\/check-in\/([0-9a-f-]{36})$/i.exec(trimmed);
  if (checkIn?.[1] !== undefined) {
    return { kind: 'check-in', receptionId: checkIn[1] };
  }

  const checkOut = /^zrsystem:\/\/check-out\/([0-9a-f-]{36})$/i.exec(trimmed);
  if (checkOut?.[1] !== undefined) {
    return { kind: 'check-out', serviceOrderId: checkOut[1] };
  }

  const serviceOrder = /^zrsystem:\/\/service-orders\/([0-9a-f-]{36})$/i.exec(trimmed);
  if (serviceOrder?.[1] !== undefined) {
    return { kind: 'service-order', orderId: serviceOrder[1] };
  }

  const quote = /^zrsystem:\/\/quotes\/([0-9a-f-]{36})$/i.exec(trimmed);
  if (quote?.[1] !== undefined) {
    return { kind: 'quote', quoteId: quote[1] };
  }

  const overtimeMatch =
    /^zrsystem:\/\/work-timer-overtime\/([0-9a-f-]{36})\/([0-9a-f-]{36})$/i.exec(trimmed);
  if (overtimeMatch?.[1] !== undefined && overtimeMatch[2] !== undefined) {
    return {
      kind: 'work-timer-overtime',
      placeId: overtimeMatch[1],
      mechanicUserId: overtimeMatch[2],
    };
  }

  return null;
}

export function staffNotificationMatchesCheckout(
  deeplink: string,
  serviceOrderId: string,
): boolean {
  const parsed = parseStaffNotificationDeeplink(deeplink);
  return parsed?.kind === 'check-out' && parsed.serviceOrderId === serviceOrderId;
}
